import { useState, useEffect, useCallback, useRef } from "react";
import { ZodSchema } from "zod";
import { apiClient, ApiError, HttpMethod } from "./apiClient";

interface UseApiOptions<TBody = any> {
  body?: TBody;
  schema?: ZodSchema;
  immediate?: boolean;
  isPublicRoute?: boolean;
}

interface ExecuteOptions {
  force?: boolean;
}

function useApiBase<TResponse = any, TBody = any>(
  url: string | null,
  method: HttpMethod,
  options: UseApiOptions<TBody> = {}
) {
  const { body, schema, immediate = method === "GET", isPublicRoute = false } = options;

  const [data, setData] = useState<TResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(immediate && url !== null);

  // Utiliser un ref pour éviter les appels multiples
  const isExecutingRef = useRef(false);
  const lastUrlRef = useRef<string | null>(null);
  const hasDataRef = useRef(false);

  const execute = useCallback(
    async (overrideBody?: TBody, executeOptions: ExecuteOptions = {}) => {
      const { force = false } = executeOptions;

      // Si url est null, ne rien faire
      if (url === null) {
        return;
      }

      // Éviter les appels multiples si déjà en cours
      if (isExecutingRef.current) {
        return;
      }

      // Type guard to ensure url is a string
      if (typeof url !== 'string') {
        const errorMsg = `useApiBase received non-string url: ${typeof url}`;
        console.error(errorMsg, url);
        const apiError = new ApiError(400, errorMsg);
        setError(apiError);
        throw apiError;
      }

      // Vérifier si l'URL a changé
      const urlChanged = lastUrlRef.current !== url;
      
      // Pour les méthodes POST/PUT/PATCH/DELETE, toujours exécuter (pas de cache)
      // Pour GET, utiliser le cache seulement si force n'est pas activé
      const shouldSkipCache = method === "GET" && !force && !urlChanged && hasDataRef.current;
      
      if (shouldSkipCache) {
        return;
      }

      // Marquer comme en cours d'exécution
      isExecutingRef.current = true;
      lastUrlRef.current = url;
      // Réinitialiser hasDataRef pour les méthodes non-GET ou si force est activé
      if (method !== "GET" || force) {
        hasDataRef.current = false;
      }
      
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient<TResponse, TBody>(url, method, {
          body: overrideBody ?? body,
          schema,
          isPublicRoute,
        });
        // Marquer comme ayant des données seulement pour GET en cas de succès
        if (method === "GET") {
          hasDataRef.current = true;
        }
        setData(result);
        return result;
      } catch (err: any) {
        // Ne pas marquer comme ayant des données en cas d'erreur
        // Cela permet de réessayer lors du prochain appel
        if (method === "GET") {
          hasDataRef.current = false;
        }
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        isExecutingRef.current = false;
      }
    },
    [url, method, body, schema, isPublicRoute]
  );

  useEffect(() => {
    // Réinitialiser le flag si l'URL change
    if (lastUrlRef.current !== url) {
      hasDataRef.current = false;
    }
    if (immediate && url !== null) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, immediate]);

  return { data, error, loading, execute };
}

// 🔹 Hooks spécialisés
export const useGet = <T>(url: string | null, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
  useApiBase<T>(url, "GET", { schema: options?.schema, immediate: true, isPublicRoute: options?.isPublicRoute });

export const usePost = <T, B = any>(url: string, options?: { schema?: ZodSchema; body?: B; isPublicRoute?: boolean }) =>
  useApiBase<T, B>(url, "POST", { schema: options?.schema, body: options?.body, immediate: false, isPublicRoute: options?.isPublicRoute });

export const usePut = <T, B = any>(url: string, options?: { schema?: ZodSchema; body?: B; isPublicRoute?: boolean }) =>
  useApiBase<T, B>(url, "PUT", { schema: options?.schema, body: options?.body, immediate: false, isPublicRoute: options?.isPublicRoute });

export const usePatch = <T, B = any>(url: string, options?: { schema?: ZodSchema; body?: B; isPublicRoute?: boolean }) =>
  useApiBase<T, B>(url, "PATCH", { schema: options?.schema, body: options?.body, immediate: false, isPublicRoute: options?.isPublicRoute });

export const useDelete = <T>(url: string, options?: { schema?: ZodSchema; isPublicRoute?: boolean }) =>
  useApiBase<T>(url, "DELETE", { schema: options?.schema, immediate: false, isPublicRoute: options?.isPublicRoute });
